import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import arrowDownIcon from '@/assets/icons/arrow-down.png';
import arrowUpIcon from '@/assets/icons/arrow-up.png';
import spainFlag from '@/assets/icons/flags/spain.png';
import ukFlag from '@/assets/icons/flags/uk.png';
import franceFlag from '@/assets/icons/flags/france.png';
import germanyFlag from '@/assets/icons/flags/germany.png';
import italyFlag from '@/assets/icons/flags/italy.png';
import portugalFlag from '@/assets/icons/flags/portugal.png';
import denmarkFlag from '@/assets/icons/flags/denmark.png';
import hollandFlag from '@/assets/icons/flags/holland.png';
import polandFlag from '@/assets/icons/flags/poland.png';
import useClickOutside from '@/hooks/useClickOutside';
import useRoutes from '@/hooks/useRoutes';
import styles from './LanguageSelector.module.scss';
import { useAppSelector } from '@/hooks/useRedux';
import getSelectedPlan from '@/utils/getSelectedPlan';

interface IProps {
    mode?: 'text' | 'flags';
}

export const LanguageSelector = ({ mode = 'text' }: IProps) => {
    const currentPlanId = useAppSelector((state) => state.plan.currentPlan);
    const [showLangSelector, setShowLangSelector] = useState(false);
    const { isLandingGeneric, isLandingMarketing, isCityGuide } = useRoutes();
    const { isAloyPlan } = getSelectedPlan(currentPlanId);
    const flagsContainer = useRef<any>(null);
    const isFlagMode = mode === 'flags';
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const currentLang = i18n.language;
    const languages = [
        {
            slug: 'en',
            name: t('english'),
            flag: ukFlag,
        },
        {
            slug: 'es',
            name: t('spanish'),
            flag: spainFlag,
        },
        {
            slug: 'de',
            name: t('deutch'),
            flag: germanyFlag,
        },
        {
            slug: 'pt',
            name: t('portuguese'),
            flag: portugalFlag,
        },
        {
            slug: 'it',
            name: t('italian'),
            flag: italyFlag,
        },
        {
            slug: 'fr',
            name: t('french'),
            flag: franceFlag,
        },
        {
            slug: 'dk',
            name: t('danish'),
            flag: denmarkFlag,
        },
        {
            slug: 'pl',
            name: t('polish'),
            flag: polandFlag,
        },
        {
            slug: 'nl',
            name: t('dutch'),
            flag: hollandFlag,
        },
    ];

    const currentLangConfig = languages.find((item) => item.slug === currentLang) ?? languages[0];
    const [lang, setLang] = useState(currentLangConfig);

    const onClickOutside = () => {
        if (showLangSelector) setShowLangSelector(false);
    };

    useClickOutside(flagsContainer, onClickOutside);

    const onLanguageMenuClick = () => {
        setShowLangSelector(!showLangSelector);
    };

    const onLanguageClick = (newLang: string) => {
        // Referencia al archivo routes del idioma actual
        const routes = i18n.getResourceBundle(currentLang, 'routes');
        // Obtiene el pathname completo de la ruta actual
        const currentPathname = window.location.pathname.replace(/\/+$/, '');
        // Busca en el archivo routes del idioma actual, la key cuyo valor sea igual al pathname actual
        let currentRouteKey = Object.keys(routes).find((key) => routes[key] === currentPathname);
        // Si está en la home no habrá path "/""y la key será undefined, en ese caso seteo la key manualmente a home
        const isHomePage = currentPathname === '' && currentRouteKey === undefined;
        if (isHomePage) currentRouteKey = 'home';
        // Obtiene el pathname con la misma key que el actual, pero en el archivo del nuevo idioma seleccionado

        setLanguageText();
        setShowLangSelector(false);

        i18n.changeLanguage(newLang).then(() => {
            if (isLandingGeneric || isLandingMarketing || isCityGuide) {
                const newLandingPath = currentPathname.replace(`/${currentLang}/`, `/${newLang}/`);
                navigate(newLandingPath);
            } else {
                const newPathName = t(`routes:${currentRouteKey}`);
                navigate(newPathName);
            }
        });
    };

    /*  const onLanguageClick = (newLang: string) => {
        setLanguageText();
        setShowLangSelector(false);
        i18n.changeLanguage(newLang);
    }; */

    const setLanguageText = () => {
        const languageUsed = i18n.use(LanguageDetector).language.split('-')[0];
        const langConfig =
            languages.find((item) => item.slug.toLowerCase() === languageUsed.toLowerCase()) ?? languages[0];

        setLang(langConfig);
    };

    useEffect(() => {
        setLanguageText();
    }, [i18n.language]);

    /* useEffect(() => {
        // Cada vez que cambie el tipo de funnel, hay que comprobar si el idioma seleccionado está disponible
        // para ese tipo de funnel, si no, cambiar al idioma por defecto (quizás al idioma del navegador del usuario)
        const aloyHasSelectedLanguage = languages.some((language) => language.slug === i18n.language);
        if (!aloyHasSelectedLanguage) {
            const languageUsed = i18n.use(LanguageDetector).language.split('-')[0];
            const langConfig =
                languages.find((item) => item.slug.toLowerCase() === languageUsed.toLowerCase()) ?? languages[0];

            onLanguageClick(langConfig.slug);
        }
    }, [isAloyPlan]); */

    const arrowIcon = showLangSelector ? arrowUpIcon : arrowDownIcon;
    const languagesToShow = languages.filter((item) => item.slug !== lang.slug);

    return (
        <div
            ref={flagsContainer}
            className={`${styles.languageSelector} ${showLangSelector && styles.active} ${
                isFlagMode && styles.languageSelectorFlags
            }`}
        >
            <button
                className={styles.flagMenuIcon}
                onClick={onLanguageMenuClick}
                aria-label={t('languageSelectorAria', { lang: lang.name })}
            >
                {isFlagMode ? <img src={lang.flag} className={styles.flag} alt="" /> : lang.name}
                <img src={arrowIcon} className={styles.arrowIcon} alt="" />
            </button>
            {showLangSelector && (
                <nav className={styles.languageNav}>
                    <ul className={styles.languageList}>
                        {languagesToShow.map((item) => (
                            <li key={item.slug} value={item.slug} onClick={() => onLanguageClick(item.slug)}>
                                {isFlagMode ? <img src={item.flag} className={styles.flag} alt="" /> : item.name}
                            </li>
                        ))}
                    </ul>
                </nav>
            )}
        </div>
    );
};

export default LanguageSelector;
